import { useCallback, useState, useMemo } from 'react';
import { FILTERS } from './useSearchFilters';
import { useClient } from '../../../../context/client';
import { useAsyncEffect } from '../../../../hooks';

const PRESET = [
	{ id: 'All Orders', title: 'All Orders' },
	{ id: 'New', title: 'New' },
	{ id: 'In Review', title: 'In Review' },
	{ id: 'Followups', title: 'Followups' },
	{ id: 'In Production', title: 'In Production' },
	{ id: 'Cancelled', title: 'Cancelled' },
	{ id: 'Shipped', title: 'Shipped' },
	{ id: 'CS Issue', title: 'CS Issue' },
];

function getOrderForRendering(orders) {
	let data = {
		data: [],
		aggregations: orders?.aggregations || {},
		count: orders?.count || 0,
	};

	if (orders?.total) {
		data.data = orders.data.map((order) => ({
			bc_order_id: order.bc_order_id ?? 'NA',
			customer_name: order.customer_name ?? 'NA',
			date: order.created ?? 'NA',
			estimatedShippingDate: order.estimatedShippingDate ?? 'NA',
			total: order.total ?? 'NA',
			vendor: order.vendors.join(', ') ?? 'NA',
			product: Array.isArray(order.product_names) ? order.product_names.filter(Boolean).join(', ') : 'NA',
			color: Array.isArray(order.colors) ? order.colors.filter(Boolean).join(', ') : 'NA',
			status: order.bc_status_id ?? 'NA',
			photos: order.photos,
			tags: order.tags ?? 'NA',
			actions: '',
			vehicles: order.vehicles ?? 'NA',
		}));
	}

	return data;
}

function countQueryResults(searchFilters) {
	const [totalOrderCount, setTotalOrderCount] = useState(0);
	const client = useClient();

	const fetchOrdersList = useCallback(async () => {
		// setIsLoading(true);

		try {
			const {
				[FILTERS.KEYWORDS]: keywords,
				[FILTERS.HAS_PHOTOS]: hasPhotos,
				...filters
			} = searchFilters;

			if (keywords) filters.search_keywords = keywords;

			filters.photos = hasPhotos;

			filters.countOnly = 'true';
			const { count } = getOrderForRendering(
				await client.getOrders(filters),
			);
			setTotalOrderCount(count);
		} catch (error) {
			console.log(error);
		} finally {
			// setIsLoading(false);
		}
	}, [searchFilters]);

	useAsyncEffect(() => fetchOrdersList(), [fetchOrdersList]);

	return [{ totalOrderCount }];
}

function useOrdersList(searchFilters) {
	const [isLoading, setIsLoading] = useState(false);
	const [orders, setOrders] = useState([]);
	const [vendors, setVendors] = useState([]);
	const [tags, setTags] = useState([]);
	const [childTags, setChildTags] = useState([]);
	const [countries, setCountries] = useState([]);
	const [aggregations, setAggregations] = useState({});
	const client = useClient();

	const fetchOrdersList = useCallback(async () => {
		setIsLoading(true);

		const {
			[FILTERS.KEYWORDS]: keywords,
			[FILTERS.SORT_FIELD]: sortField,
			[FILTERS.SORT_ORDER]: sortOrder,
			[FILTERS.HAS_PHOTOS]: hasPhotos,
			...filters
		} = searchFilters;

		if (keywords) filters.search_keywords = keywords;

		filters.photos = hasPhotos;
		
		// Always include sort parameters
		filters.sort_field = sortField || 'date'; // Provide a default value
		filters.sort_order = sortOrder || 'desc'; // Provide a default value

		try {
			const response = await client.getOrders(filters) ?? {};
			const { data = [], aggregations } = getOrderForRendering(response);

			setOrders(data);
			if (aggregations) setAggregations(aggregations);
			if (aggregations?.vendor) setVendors(aggregations.vendor);
			if (aggregations?.tags) setTags(aggregations.tags);
			if (aggregations?.country) setCountries(aggregations.country);
			if (aggregations?.sub_tags) setChildTags(aggregations.sub_tags);
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	}, [searchFilters]);

	useAsyncEffect(() => fetchOrdersList(), [fetchOrdersList]);

	// Client-side sorting
	const sortedOrders = useMemo(() => {
		if (!searchFilters[FILTERS.SORT_FIELD] || !searchFilters[FILTERS.SORT_ORDER]) {
			return orders; // Return unsorted orders if no sorting is applied
		}

		const sortField = searchFilters[FILTERS.SORT_FIELD];
		const sortOrder = searchFilters[FILTERS.SORT_ORDER];

		return [...orders].sort((a, b) => {
			const aValue = a[sortField] || [];
			const bValue = b[sortField] || [];
			if (aValue.length < bValue.length) return sortOrder === 'asc' ? 1 : -1;
			if (aValue.length > bValue.length) return sortOrder === 'asc' ? -1 : 1;
			return 0;
		});
	}, [orders, searchFilters[FILTERS.SORT_FIELD], searchFilters[FILTERS.SORT_ORDER]]);

	return [
		{ orders: sortedOrders, vendors, tags, countries, childTags, aggregations },
		isLoading,
		fetchOrdersList,
	];
}

export { useOrdersList, countQueryResults, PRESET };